import { ReactNode, RefObject, useId } from 'react';
import { useTranslation } from 'react-i18next';
import AppLabel from '../app-label/app-label';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { ReactComponent as ErrorIcon } from '../../assets/svg/errorIcon.svg';
import { ReactComponent as CloseIcon } from '../../assets/svg/CloseIcon.svg';
import styles from './OrgNumberField.module.scss';

type Props = {
  value: string;
  errorMessage?: string | null;
  requiredMessage?: string;
  note?: ReactNode;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
  onClear: () => void;
  inputRef?: RefObject<HTMLInputElement>;
};

export default function OrgNumberField({
  value,
  errorMessage,
  requiredMessage,
  note,
  onChange,
  onBlur,
  onClear,
  inputRef,
}: Props) {
  const [t] = useTranslation('organizations', { keyPrefix: 'add' });
  const errorId = useId();

  return (
    <>
      <AppLabel
        class="ar-label"
        headline={t('label.organizationNumber', 'Organisationsnummer')}
        required
      >
        <div className={styles.field}>
          <input
            ref={inputRef}
            className={`ar-text-field ${styles.input} ${errorMessage ? styles.invalid : ''}`}
            name="organizationNumber"
            value={value}
            aria-invalid={!!(errorMessage || requiredMessage)}
            aria-describedby={errorMessage ? errorId : undefined}
            onChange={event => onChange(event.target.value)}
            onBlur={event => onBlur(event.target.value)}
          />
          {errorMessage && (
            <div className={styles.indicators}>
              <TooltipContainer
                renderReferenceComponent={(className, ref) => (
                  <ErrorIcon className={className} ref={ref} />
                )}
              >
                <span id={errorId}>{errorMessage}</span>
              </TooltipContainer>
              <button
                type="button"
                className={styles.clear}
                onClick={onClear}
                aria-label={t('button.clearOrganizationNumber', 'Rensa')}
              >
                <CloseIcon />
              </button>
            </div>
          )}
        </div>
        {requiredMessage && <div className="error show">{requiredMessage}</div>}
      </AppLabel>
      {note && (
        <p className={`${styles.note} ${requiredMessage ? styles.afterError : ''}`}>{note}</p>
      )}
    </>
  );
}
